<script setup lang="ts">
import { isGifUrl } from '@@/bits/image'
import { ANONYMOUS_AVATAR } from '@@/bits/user_model'
import { defineAsyncComponent } from '@@/bits/vue'
import { processedUrl } from '@padlet/vivaldi-client'
import { computed, ref } from 'vue'

// ImageThumbnailGif transitively imports the Surface stores (surface_current_user,
// surface_attachments), which pull the entire Surface app graph into any bundle that
// statically references OzAvatar — including the landside marketing entry. GIF avatars
// are rare and never appear in the anonymous marketing header, so load the GIF renderer
// on demand instead of at boot.
const ImageThumbnailGif = defineAsyncComponent(() => import('@@/vuecomponents/ImageThumbnailGif.vue'))

const {
  width,
  height,
  src = null,
  fallbackSrc = ANONYMOUS_AVATAR,
} = defineProps<{
  /**
   * width of the avatar
   */
  width?: number
  /**
   * height of the avatar
   */
  height?: number
  /**
   * Image source URL
   */
  src?: string | null
  /**
   * Fallback image source URL
   */
  fallbackSrc?: string
}>()

const isErrored = ref(false)

const processedSrc = computed(() => {
  if (!src || isErrored.value) return fallbackSrc

  // for relative urls e.g. when developing locally
  if (src.startsWith('/')) return src

  return processedUrl(src, transforms.value)
})

const transforms = computed(() => {
  if (width && height) {
    return {
      width: width,
      height: height,
      preset: 'avatar',
    }
  }
  return {
    preset: 'avatar',
  }
})

const isGif = computed(() => isGifUrl(src))
</script>

<script lang="ts">
export default {}
</script>

<template>
  <ImageThumbnailGif
    v-if="isGif && src"
    :image-src="processedSrc"
    :width="width || 40"
    :height="height || 40"
    :original-image-width="width || 40"
    :original-image-height="height || 40"
    :rounded-class="'rounded-full'"
    :x-play-pause-button="false"
    aria-hidden="true"
  />
  <img v-else aria-hidden="true" :width="width" :height="height" alt="" :src="processedSrc" @error="isErrored = true" />
</template>
